import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type DependencyList,
} from 'react';

// Default height (px) of the soft fade at each end of the scroll area. Spans
// well over a row so content eases out gradually rather than hard-clipping.
const DEFAULT_FADE_PX = 72;
// Gradient stops sampled per faded edge (smoothstep is evaluated this many times).
const FADE_STOPS = 8;
// Time-constant of the exponential ease (ms); larger = slower fade in/out.
const FADE_TAU_MS = 90;

// Smoothstep: zero slope at both ends, so the fade has no visible kink at the
// outer edge or where it meets the opaque content (a linear ramp leaves one).
function smoothstep(t: number): number {
  return t * t * (3 - 2 * t);
}

// Build the edge-fade mask. `top`/`bottom` are 0→1 depths (0 = no fade on that
// side, 1 = full fade); the mask works on alpha so the content fades, not the
// background — which matters over the kiosk cards' radial-gradient wash.
function buildFadeMask(fadePx: number, top: number, bottom: number): string {
  const F = fadePx;
  // alpha at fraction `q` (0 = outer edge → 1 = inner/opaque), scaled by depth
  // so a partially-faded side is only partially transparent.
  const alpha = (q: number, depth: number) =>
    Math.round((1 - depth * (1 - smoothstep(q))) * 1000) / 1000;
  const stops: string[] = [];
  for (let i = 0; i <= FADE_STOPS; i++) {
    const q = i / FADE_STOPS;
    stops.push(`rgba(0,0,0,${alpha(q, top)}) ${(F * q).toFixed(1)}px`);
  }
  stops.push(`#000 calc(100% - ${F}px)`);
  for (let i = FADE_STOPS; i >= 0; i--) {
    const q = i / FADE_STOPS; // inner→edge as i decreases
    stops.push(`rgba(0,0,0,${alpha(q, bottom)}) calc(100% - ${(F * q).toFixed(1)}px)`);
  }
  return `linear-gradient(to bottom, ${stops.join(', ')})`;
}

export interface ScrollFade {
  /** Attach to the scrollable element (callback ref). */
  ref: (node: HTMLDivElement | null) => void;
  /** Wire to the element's onScroll. */
  onScroll: () => void;
  /** Spread onto the element's style — sets the fade mask. */
  style: CSSProperties;
}

/**
 * Soft, time-based top/bottom edge fades for a vertical scroll container, shared
 * by the kiosk's scrolling lists (Upcoming, AnyList, MusicBrowserModal).
 *
 * Each edge fades in to full strength — eased over time, not scaled by scroll
 * position — whenever that side has overflow, and eases back out at the extremes
 * so the first/last items stay fully readable. Pass `deps` that change when the
 * scrollable content changes so overflow is re-measured; resizes are handled
 * automatically via ResizeObserver.
 */
export function useScrollFade(
  deps: DependencyList = [],
  fadePx: number = DEFAULT_FADE_PX,
): ScrollFade {
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // The element is also tracked in state so the effects that must (re)bind to
  // the DOM node — grab-to-scroll and overflow measuring — re-run when the node
  // mounts or swaps (e.g. the modal's view-switched lists), NOT on every content
  // re-render. Keying those on unstable content deps tore down in-progress drags
  // (a fade-driven re-render mid-drag removed the pointer listeners).
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const setScrollRef = useCallback((node: HTMLDivElement | null) => {
    scrollRef.current = node;
    setScrollEl(node);
  }, []);
  const [fade, setFade] = useState({ top: 0, bottom: 0 });
  const fadeRef = useRef({ top: 0, bottom: 0 }); // live depth, drives the tween
  const target = useRef({ top: 0, bottom: 0 });
  const rafId = useRef<number | null>(null);
  const lastTs = useRef<number | null>(null);

  // rAF loop: ease the live depth toward the target, mirror it into state to
  // re-render, and stop once it has arrived. No side effects inside setState.
  const tick = useCallback((ts: number) => {
    const dt = ts - (lastTs.current ?? ts);
    lastTs.current = ts;
    const k = 1 - Math.exp(-dt / FADE_TAU_MS); // frame-rate-independent step
    const cur = fadeRef.current;
    const t = target.current;
    let top = cur.top + (t.top - cur.top) * k;
    let bottom = cur.bottom + (t.bottom - cur.bottom) * k;
    if (Math.abs(top - t.top) < 0.004) top = t.top;
    if (Math.abs(bottom - t.bottom) < 0.004) bottom = t.bottom;
    fadeRef.current = { top, bottom };
    setFade({ top, bottom });
    if (top !== t.top || bottom !== t.bottom) {
      rafId.current = requestAnimationFrame(tick);
    } else {
      rafId.current = null;
      lastTs.current = null;
    }
  }, []);

  const updateFade = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollHeight - el.clientHeight;
    target.current = {
      top: el.scrollTop > 1 ? 1 : 0,
      bottom: maxScroll - el.scrollTop > 1 ? 1 : 0,
    };
    if (rafId.current == null) {
      lastTs.current = null;
      rafId.current = requestAnimationFrame(tick);
    }
  }, [tick]);

  // Re-measure overflow when content changes (deps) or the element resizes.
  useEffect(() => {
    updateFade();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(() => updateFade());
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollEl, updateFade, ...deps]);

  // Cancel any in-flight frame on unmount; reset the refs so a StrictMode
  // remount re-schedules the tween instead of thinking one is in flight.
  useEffect(
    () => () => {
      if (rafId.current != null) cancelAnimationFrame(rafId.current);
      rafId.current = null;
      lastTs.current = null;
    },
    [],
  );

  // Grab-to-scroll. The wall-mounted kiosk's touchscreen is delivered to
  // Chromium as a mouse pointer, so a finger swipe arrives as a pointer drag —
  // which natively only moves the scrollbar, never the content, forcing you to
  // grab the thin bar. This lets you drag anywhere on the list to pan it, and
  // swallows the click that ends a drag so panning never activates a row. Paired
  // with `touchAction: 'none'` (below) so genuine touch doesn't also scroll and
  // double up — this becomes the single scroller. Mouse wheel + scrollbar drag
  // are unaffected. Re-attaches on `deps` so lists that mount later (the modal's
  // view-switched lists) get wired once their element exists.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let dragging = false;
    let startY = 0;
    let startScroll = 0;
    let moved = false;

    const onMove = (e: PointerEvent) => {
      if (!dragging) return;
      const dy = e.clientY - startY;
      if (Math.abs(dy) > 4) moved = true;
      el.scrollTop = startScroll - dy; // fires onScroll → fade stays in sync
    };
    const onUp = () => {
      dragging = false;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    const onDown = (e: PointerEvent) => {
      if (e.button !== 0) return; // primary button / touch / pen only
      dragging = true;
      moved = false;
      startY = e.clientY;
      startScroll = el.scrollTop;
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    };
    // Capture phase so we intercept the post-drag click before it reaches a row
    // (and before React's bubble-phase onClick at the root), but only when an
    // actual drag happened — taps pass through untouched.
    const onClickCapture = (e: MouseEvent) => {
      if (moved) {
        e.stopPropagation();
        e.preventDefault();
        moved = false;
      }
    };

    el.addEventListener('pointerdown', onDown);
    el.addEventListener('click', onClickCapture, true);
    return () => {
      el.removeEventListener('pointerdown', onDown);
      el.removeEventListener('click', onClickCapture, true);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
    // Bound to the element, not content: re-runs only when the node mounts or
    // swaps, so a fade-driven re-render mid-drag never tears down the listeners.
  }, [scrollEl]);

  const mask = buildFadeMask(fadePx, fade.top, fade.bottom);
  return {
    ref: setScrollRef,
    onScroll: updateFade,
    // touchAction 'none' hands all touch panning to the grab-to-scroll handler
    // above (the kiosk's touch is pointer-emulated anyway), so there's no
    // double-scroll on devices that do report native touch.
    style: { WebkitMaskImage: mask, maskImage: mask, touchAction: 'none' },
  };
}
